import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { ServerApi } from '@services/server-api';
import { FleetCardsFeatureFlagService } from '@app/services/feature-flags/fleet-cards';

import { extractResponseBody } from '@lib/utils';
import { ICustomFieldsServerResponse, ITransactionMetadata,
  IPreTransactionMetadataServerResponse,
  IPostTransactionMetadataServerResponse} from '../shared/fleet-cards-waitlist/fleet-cards-waitlist.interface';


export interface IMetaNonCompatiblePhoneServerResponse {
  required_app_version?: string;
  users_with_phone_and_older_app_version?: IMissingPhoneDriversServerResponse;
  users_without_phone_and_older_app_version?: IMissingPhoneDriversServerResponse;
  required_app_version_pre_txn_metadata?: string;
  required_app_version_post_txn_metadata?: string;
  users_with_phone_and_older_app_version_pre_txn_metadata?: IMissingPhoneDriversServerResponse;
  users_without_phone_and_older_app_version_pre_txn_metadata?: IMissingPhoneDriversServerResponse;
  users_with_phone_and_older_app_version_post_txn_metadata?: IMissingPhoneDriversServerResponse;
  users_without_phone_and_older_app_version_post_txn_metadata?: IMissingPhoneDriversServerResponse;
  cards_with_overrides?: number;
}

export interface IMissingPhoneDriversServerResponse {
  drivers: IMetaDriverServerResponse[];
}

export interface IMetaDriverServerResponse {
  id: string;
  full_name: string;
  last_four_digits: string;
  driver_company_id: string;
  app_version?: string;
  device_platform?: string;
  mobile_last_active_at?: string;
}

export interface IMetaNonCompatiblePhone {
  appVersion: string,
  driversOldAppWithPhone: IMetaDriver[];
  driversOldAppWithOutPhone: IMetaDriver[];
  appVersionPostTxn: string,
  driversOldAppWithPhonePostTxn: IMetaDriver[];
  driversOldAppWithOutPhonePostTxn: IMetaDriver[];
  totalWithOldVersionPostTxn: number;
  cardsWithOverrides?: number;
}

export interface IMetaDriver {
  id: string;
  fullName: string;
  lastFourDigits?: string;
  driverCompanyId: string;
  appVersion?: string;
  devicePlatform?: string;
  mobileLastActiveAt?: string;
}

export enum META_NON_COMPATIBLE_PHONE_KEYS {
  CUSTOM_CONFIG = 'customConfig',
  VEHICLE_REPORTING = 'vehicleReporting',
  ODOMETER_REPORTING = 'odometerReporting',
  ASSET_VEHICLE_REPORTING = 'assetVehicleReporting',
}

@Injectable()
export class PreTransactionMetadataService {
  private isFleetCardsCaptureAssetInfoOnTransactionsEnabled: boolean = false;

  constructor(
    private api: ServerApi,
    private featureFlagService: FleetCardsFeatureFlagService,
  ) {
    this.isFleetCardsCaptureAssetInfoOnTransactionsEnabled =
      this.featureFlagService.isFleetCardsCaptureAssetInfoOnTransactionsEnabled();
  }

  public editTransactionMetadata(
    transactionMetadata: IPreTransactionMetadataServerResponse,
    postTransactionMetadata: IPostTransactionMetadataServerResponse,
  ): Observable<ITransactionMetadata> {
    return this.api.post(`fleetcard/w1/txn_metadata_settings/transaction_metadata`, {
      transaction_metadata: transactionMetadata, // TODO : This will be removed with the post txn FF
      pre_transaction_metadata: transactionMetadata,
      post_transaction_metadata: postTransactionMetadata,
    })
    .pipe(
      map(extractResponseBody),
      map(this.transformMetaData.bind(this)),
    );
  }

  public getMissingPhoneMetadata(): Observable<IMetaNonCompatiblePhone> {
    return this.api.get(`fleetcard/w1/cardholders/transaction_metadata/not_compatible`)
      .pipe(
        map(extractResponseBody),
        map(this.transformMetaNonCompatiblePhoneData.bind(this)),
      );
  }

  private transformMetaData(data: {
    transaction_metadata?: IPreTransactionMetadataServerResponse ,
    pre_transaction_metadata?: IPreTransactionMetadataServerResponse,
    post_transaction_metadata?: IPostTransactionMetadataServerResponse,
  }): ITransactionMetadata {
    if (data) {
      const preTxnMetadata = data?.transaction_metadata || data?.pre_transaction_metadata;
      const postTxnMetadata = data?.post_transaction_metadata;

      const transactionMetadata: ITransactionMetadata = {
        ...(!this.isFleetCardsCaptureAssetInfoOnTransactionsEnabled && {
          vehicleReporting: {
            status: preTxnMetadata?.vehicle_reporting.status,
            acceptAnyVehicleId: preTxnMetadata?.vehicle_reporting.accept_any_vehicle_id,
            manualEntry: preTxnMetadata?.vehicle_reporting.manual_entry,
          },
          odometerReporting: {
            status: preTxnMetadata?.odometer_reporting.status,
            manualEntry: preTxnMetadata?.odometer_reporting.manual_entry,
          },
        }),
        ...(this.isFleetCardsCaptureAssetInfoOnTransactionsEnabled && {
          assetVehicleReporting: {
            status: preTxnMetadata?.asset_vehicle_reporting_settings?.status,
            odometerReporting: preTxnMetadata?.asset_vehicle_reporting_settings?.odometer_reporting,
            autoPopulateVGValues: preTxnMetadata?.asset_vehicle_reporting_settings?.auto_populate_vg_values,
            validateReportedValuesInSms: preTxnMetadata?.asset_vehicle_reporting_settings?.input_validation_in_sms,
          },
        }),
        customFields: preTxnMetadata?.custom_fields?.map((customField: ICustomFieldsServerResponse) => ({
          id: customField.id,
          status: customField.status,
          name: customField.name,
          valueType: customField.value_type,
          values: customField.values,
        })),
        receiptRequired: postTxnMetadata?.receipt_required?.status,
      };

      return transactionMetadata;
    }
  }

  private transformMetaNonCompatiblePhoneData(data: IMetaNonCompatiblePhoneServerResponse): IMetaNonCompatiblePhone {
    type MapDriverFunction = (driver: IMetaDriverServerResponse) => IMetaDriver;
    const mapDriver: MapDriverFunction = (driver: IMetaDriverServerResponse) => ({
      id: driver.id,
      fullName: driver.full_name,
      lastFourDigits: driver.last_four_digits,
      driverCompanyId: driver.driver_company_id,
      appVersion: driver?.app_version ?? null,
      devicePlatform: driver?.device_platform ?? null,
      mobileLastActiveAt: driver?.mobile_last_active_at ?? null,
    });
    return {
      // TODO:  remove data?.required_app_version when clear feature flag
      appVersion:
        data?.required_app_version_pre_txn_metadata ?
        data?.required_app_version_pre_txn_metadata :
        data?.required_app_version,

      driversOldAppWithPhone: data?.users_with_phone_and_older_app_version_pre_txn_metadata ?
        data?.users_with_phone_and_older_app_version_pre_txn_metadata?.drivers.map(mapDriver) ?? [] :
        data?.users_with_phone_and_older_app_version?.drivers.map(mapDriver) ?? [],
        // TODO:  remove users_without_phone_and_older_app_version when clear feature flag

      driversOldAppWithOutPhone: data?.users_without_phone_and_older_app_version_pre_txn_metadata ?
        data?.users_without_phone_and_older_app_version_pre_txn_metadata?.drivers.map(mapDriver) ?? [] :
        data?.users_without_phone_and_older_app_version?.drivers.map(mapDriver) ?? [],
        // TODO:  remove users_without_phone_and_older_app_version when clear feature flag

      appVersionPostTxn: data?.required_app_version_post_txn_metadata,

      driversOldAppWithPhonePostTxn:
        data?.users_with_phone_and_older_app_version_post_txn_metadata?.drivers.map(mapDriver) ??
        [],

      driversOldAppWithOutPhonePostTxn:
        data?.users_without_phone_and_older_app_version_post_txn_metadata?.drivers.map(mapDriver) ??
        [],

      totalWithOldVersionPostTxn:
        (data?.users_with_phone_and_older_app_version_post_txn_metadata?.drivers?.length || 0) +
        (data?.users_without_phone_and_older_app_version_post_txn_metadata?.drivers?.length || 0),

      cardsWithOverrides: data.cards_with_overrides || 0,
    };
  }
}